import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { MonitorSmartphone, Smartphone, Monitor, Trash2, LogOut } from "lucide-react";

const osIcon = (os) => (os === "iOS" || os === "Android" ? Smartphone : Monitor);

const timeAgo = (iso) => {
  if (!iso) return "—";
  const d = (Date.now() - new Date(iso).getTime()) / 1000;
  if (d < 3600) return "just now";
  if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
  return `${Math.floor(d / 86400)}d ago`;
};

export const DevicesSection = () => {
  const [devices, setDevices] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/auth/devices").then(({ data }) => setDevices(data.devices)).catch(() => setDevices([]));
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    try {
      await api.delete(`/auth/devices/${id}`);
      toast.success("Device removed — you'll get an alert if it signs in again");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const logoutAll = async () => {
    setBusy(true);
    try {
      await api.post("/auth/logout-all");
      toast.success("Every other device has been signed out");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-6 rounded-xl border border-[#27272A] bg-[#121212] p-6" data-testid="devices-section">
      <div className="flex items-center gap-2">
        <MonitorSmartphone className="h-4 w-4 text-[#FF3B30]" />
        <h2 className="font-bold text-lg">Signed-in devices</h2>
      </div>
      <p className="mt-2 text-sm text-neutral-400">
        Devices we've seen on your account. Spot a stranger? Remove it, then sign out everywhere and reset your password.
      </p>
      <div className="mt-4 space-y-2">
        {devices === null && <div className="text-sm text-neutral-500">Loading…</div>}
        {devices?.length === 0 && <div className="text-sm text-neutral-500">No devices recorded yet.</div>}
        {devices?.map((d) => {
          const Icon = osIcon(d.os);
          return (
            <div key={d.id} data-testid={`device-row-${d.id}`} className="flex items-center gap-3 rounded-lg border border-[#27272A] bg-black/30 px-4 py-3">
              <Icon className="h-4 w-4 text-neutral-400 flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold truncate">
                  {d.browser} on {d.os}
                  {d.current && <span className="ml-2 text-[10px] uppercase tracking-wider text-[#34C759] border border-[#34C759]/40 rounded-full px-2 py-0.5">This device</span>}
                </div>
                <div className="text-xs text-neutral-500">Last seen {timeAgo(d.last_seen)} · IP {d.last_ip}</div>
              </div>
              {!d.current && (
                <Button
                  variant="ghost"
                  size="sm"
                  data-testid={`remove-device-btn-${d.id}`}
                  onClick={() => remove(d.id)}
                  className="text-[#FF3B30] hover:text-[#FF3B30] hover:bg-[#FF3B30]/10 h-8 px-3"
                >
                  <Trash2 className="h-3.5 w-3.5 mr-1" /> Remove
                </Button>
              )}
            </div>
          );
        })}
      </div>
      <Button
        onClick={logoutAll}
        disabled={busy}
        data-testid="logout-all-btn"
        className="mt-4 rounded-full bg-[#FF3B30] hover:bg-[#e0342a] text-white h-10 px-6 font-semibold"
      >
        <LogOut className="h-4 w-4 mr-2" />
        {busy ? "Signing out…" : "Sign out everywhere else"}
      </Button>
    </div>
  );
};
